import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Typography, Select, Row, Col, Space, Button, Tag, message, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import StandardTable from '../StandardTable';
import { fetchSqlData, fetchSqlDataWithQuery, deleteData } from '../../../services/api';
import type { CorFormGroup } from '../../../types';

const { Title } = Typography;

function getModuleFromPath(pathname: string): string {
  const match = pathname.match(/^\/(\w+)/);
  return match ? match[1] : '';
}

function getViewNameForModule(module: string): string {
  return module ? `vw_${module}_form_records_details` : '';
}

// Resolve the base records table for a module (used for deletes)
function getDataTableForModule(module: string): string {
  switch (module) {
    case 'qaqc':
      return 'qaqc_form_records';
    case 'it':
      return 'it_form_records';
    case 'to':
      return 'to_form_records';
    case 'hse':
      return 'hse_form_records';
    case 'admin':
      return 'adm_form_records';
    case 'machinery':
      return 'mach_form_records';
    case 'material':
      return 'mat_form_records';
    case 'construction':
      return 'const_form_records';
    case 'dcc':
      return 'dcc_form_records';
    case 'official':
      return 'off_form_records';
    case 'design':
      return 'dsgn_form_records';
    default:
      return '';
  }
}

const getStatusColor = (status: string | undefined | null): string => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'draft':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'active':
      return 'success';
    case 'inactive':
      return 'warning';
    case 'terminated':
      return 'error';
    default:
      return 'default';
  }
};

const formatEnumString = (value: string | undefined | null): string => {
  if (!value) return '-';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const FormRecordDetailsList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const module = getModuleFromPath(location.pathname);
  const viewName = getViewNameForModule(module);

  const [groups, setGroups] = useState<CorFormGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [essentialFields, setEssentialFields] = useState<string[]>([]);
  const [columns, setColumns] = useState<ColumnsType<any>>([]);
  const [viewColumns, setViewColumns] = useState<{ column_name: string; data_type: string }[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters state (no essentials free-text here)
  const [filters, setFilters] = useState<{
    form_ref: string;
    form_num: string;
    form_date: any | null; // dayjs
    status: string[];
    created_by: string;
  }>({
    form_ref: '',
    form_num: '',
    form_date: null,
    status: [],
    created_by: '',
  });

  // Generic column filter state
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>(undefined);
  const [columnQuery, setColumnQuery] = useState<string>('');

  // Fetch available form groups for this module
  useEffect(() => {
    const fetchGroups = async () => {
      const moduleRoute = '/' + (module || '');
      const res: CorFormGroup[] = await fetchSqlData('cor_form_groups', 'route_name', moduleRoute);
      setGroups(Array.isArray(res) ? res : []);
      // Try restore previously selected group for this module from sessionStorage
      try {
        const key = `form_details_selected_group:${moduleRoute}`;
        const saved = sessionStorage.getItem(key);
        if (saved) {
          const num = Number(saved);
          if (!Number.isNaN(num)) setSelectedGroupId(num);
        }
      } catch {}
    };
    fetchGroups();
  }, [module]);

  // Track essential fields of the selected group
  useEffect(() => {
    if (!selectedGroupId) return;
    const g = groups.find(x => x.id === selectedGroupId);
    setEssentialFields(g?.essential_field_names || []);
  }, [selectedGroupId, groups]);

  // Fetch column list for the VIEW from information_schema, then fetch records
  useEffect(() => {
    const reset = () => {
      setColumns([]);
      setData([]);
      setViewColumns([]);
    };

    if (!viewName || !selectedGroupId) {
      reset();
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        // 1) Fetch column names and data types from information_schema for the view
        const colSql = `
          SELECT column_name, data_type
          FROM information_schema.columns
          WHERE table_schema = 'public' AND table_name = '${viewName}'
          ORDER BY ordinal_position
        `;
        const cols = await fetchSqlDataWithQuery(colSql);
        const normalizedCols: { column_name: string; data_type: string }[] = Array.isArray(cols) ? cols : [];
        setViewColumns(normalizedCols);

        // 2) Build base AntD columns manually (we will add dynamic key columns later)
        const baseColumns: ColumnsType<any> = [
          { title: 'Form Ref', dataIndex: 'form_ref', key: 'form_ref', ellipsis: true },
          { title: 'Form Num', dataIndex: 'form_num', key: 'form_num', ellipsis: true },
          { title: 'Form Date', dataIndex: 'form_date', key: 'form_date', ellipsis: true },
          { title: 'Status', dataIndex: 'status', key: 'status', ellipsis: true, render: (val: string) => <Tag color={getStatusColor(val)}>{formatEnumString(val)}</Tag> },
          { title: 'Form Description', dataIndex: 'form_description', key: 'form_description', ellipsis: true },
          { title: 'Created By', dataIndex: 'created_by', key: 'created_by', ellipsis: true },
        ];

        const actionCol = {
          title: 'Actions',
          key: 'actions',
          render: (_: any, record: any) => (
            <Space>
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => navigate(`/${module}/forms/view/${record.id}`)}
              />
              {['draft','submitted'].includes(String(record?.status || '').toLowerCase()) && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => navigate(`/${module}/forms/edit/${record.id}`)}
                />
              )}
              <Popconfirm
                title="Are you sure you want to delete this record?"
                onConfirm={async () => {
                  try {
                    const dataTable = getDataTableForModule(module);
                    if (!dataTable) {
                      message.error('Delete not supported for this module.');
                      return;
                    }
                    await deleteData(dataTable, 'id', Number(record.id));
                    const refreshed = await fetchSqlDataWithQuery(`SELECT * FROM ${viewName} WHERE group_id = ${selectedGroupId} ORDER BY id DESC`);
                    setData(Array.isArray(refreshed) ? refreshed : []);
                    message.success('Record deleted');
                  } catch (err) {
                    message.error('Failed to delete record');
                  }
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            </Space>
          ),
          width: 120,
          fixed: 'right' as const,
        };

        // 3) Fetch data (rows contain repeated records per key/value)
        const dataSql = `SELECT * FROM ${viewName} WHERE group_id=${selectedGroupId} ORDER BY id DESC`;
        const result = await fetchSqlDataWithQuery(dataSql);
        const arrayData = Array.isArray(result) ? result : [];

        // 3a) Derive distinct dynamic keys from the key/value pairs
        const dynamicKeys: string[] = Array.from(new Set(arrayData.map((r: any) => r?.key).filter((k: any) => typeof k === 'string' && k.trim().length > 0)));

        // 3b) Pivot rows into a single row per record id, assigning key/value pairs as columns
        const byId = new Map<number | string, any>();
        for (const r of arrayData) {
          const recId = r?.id;
          if (recId == null) continue;
          let row = byId.get(recId);
          if (!row) {
            row = {
              id: r.id,
              group_id: r.group_id,
              form_ref: r.form_ref,
              form_num: r.form_num,
              form_date: r.form_date,
              status: r.status,
              form_description: r.form_description,
              created_by: r.created_by,
            };
            byId.set(recId, row);
          }
          const k = r?.key;
          const v = r?.value;
          if (typeof k === 'string' && k) {
            row[k] = v;
          }
        }
        const pivotedData = Array.from(byId.values());
        setData(pivotedData);

        // 4) Build dynamic key columns and auto-remove empty ones
        const dynamicColumns: ColumnsType<any> = dynamicKeys.map((k) => ({
          title: k.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          dataIndex: k,
          key: k,
          ellipsis: true,
        }));

        const builtCols: ColumnsType<any> = [...baseColumns, ...dynamicColumns];

        const alwaysKeep = new Set(['id', 'form_ref', 'form_num', 'form_date', 'status', 'form_description', 'created_by', 'group_id']);
        const nonEmptyCols = builtCols.filter((col: any) => {
          const key = String(col.dataIndex ?? col.key ?? '');
          if (alwaysKeep.has(key)) return true;
          // If any row has a non-null, non-empty value, keep the column
          return (pivotedData || []).some((row: any) => {
            const v = row?.[key];
            if (v === null || v === undefined) return false;
            if (typeof v === 'string') return v.trim().length > 0;
            // Numbers, booleans, dates, objects are considered non-empty when not null/undefined
            return true;
          });
        });

        setColumns([...nonEmptyCols, actionCol]);
      } catch (e) {
        message.error('Failed to fetch view columns or records');
        reset();
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [viewName, selectedGroupId, module, navigate]);

  // Stable unique keys for rows to avoid pagination leakage
  // Apply cascading filters to data
  const filteredData = useMemo(() => {
    return (data || []).filter((record: any) => {
      if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
      if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
      if (filters.form_date) {
        const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
        const recDate = String(record.form_date || '').slice(0, 10);
        if (picked && recDate !== picked) return false;
      }
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
      }
      if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
      // Selected column contains filter
      if (selectedColumn && columnQuery.trim() !== '') {
        const val = record?.[selectedColumn];
        const hay = val == null ? '' : String(val).toLowerCase();
        if (!hay.includes(columnQuery.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, filters, selectedColumn, columnQuery]);

  // Stable unique keys for rows to avoid pagination leakage
  const dataWithStableKey = useMemo(() => {
    return (filteredData || []).map((r: any, idx: number) => {
      const baseKey =
        r?.id != null ? String(r.id)
        : r?.form_record_id != null ? String(r.form_record_id)
        : r?.form_id != null ? String(r.form_id)
        : r?.form_ref != null && r?.form_date != null ? `${String(r.form_ref)}::${String(r.form_date)}`
        : `${String(r.form_ref ?? '')}::${String(r.created_by ?? '')}::${String(r.form_num ?? '')}`;
      const key = `${baseKey}::${idx}`;
      return { ...r, _rowKey: key };
    });
  }, [filteredData]);

  // Cascading options for selects
  const statusOptions = useMemo(() => {
    const pre = (data || []).filter((record: any) => {
      if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
      if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
      if (filters.form_date) {
        const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
        const recDate = String(record.form_date || '').slice(0, 10);
        if (picked && recDate !== picked) return false;
      }
      if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
      return true;
    });
    return Array.from(new Set(pre.map((r: any) => String(r.status || '')).filter((s: string) => s.length > 0)));
  }, [data, filters.form_ref, filters.form_num, filters.form_date, filters.created_by]);

  const formRefOptions = useMemo(() => {
    const pre = (data || []).filter((record: any) => {
      if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
      if (filters.form_date) {
        const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
        const recDate = String(record.form_date || '').slice(0, 10);
        if (picked && recDate !== picked) return false;
      }
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
      }
      if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
      return true;
    });
    return Array.from(new Set(pre.map((r: any) => String(r.form_ref || '')).filter((s: string) => s.length > 0)));
  }, [data, filters.form_num, filters.form_date, filters.status, filters.created_by]);

  const formNumOptions = useMemo(() => {
    const pre = (data || []).filter((record: any) => {
      if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
      if (filters.form_date) {
        const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
        const recDate = String(record.form_date || '').slice(0, 10);
        if (picked && recDate !== picked) return false;
      }
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
      }
      if (filters.created_by && !String(record.created_by || '').toLowerCase().includes(filters.created_by.toLowerCase())) return false;
      return true;
    });
    return Array.from(new Set(pre.map((r: any) => String(r.form_num || '')).filter((s: string) => s.length > 0)));
  }, [data, filters.form_ref, filters.form_date, filters.status, filters.created_by]);

  const createdByOptions = useMemo(() => {
    const pre = (data || []).filter((record: any) => {
      if (filters.form_ref && !String(record.form_ref || '').toLowerCase().includes(filters.form_ref.toLowerCase())) return false;
      if (filters.form_num && !String(record.form_num || '').toLowerCase().includes(filters.form_num.toLowerCase())) return false;
      if (filters.form_date) {
        const picked = filters.form_date.format ? filters.form_date.format('YYYY-MM-DD') : String(filters.form_date);
        const recDate = String(record.form_date || '').slice(0, 10);
        if (picked && recDate !== picked) return false;
      }
      if (Array.isArray(filters.status) && filters.status.length > 0) {
        if (!filters.status.map(String).map(s => s.toLowerCase()).includes(String(record.status || '').toLowerCase())) return false;
      }
      return true;
    });
    return Array.from(new Set(pre.map((r: any) => String(r.created_by || '')).filter((s: string) => s.length > 0)));
  }, [data, filters.form_ref, filters.form_num, filters.form_date, filters.status]);

  // Build column options from current columns (exclude actions)
  const columnOptions = useMemo(() => {
    const excluded = new Set(['form_ref','form_num','form_date','status','created_by','action','actions','id','group_id']);
    return (columns || [])
      .map((c: any) => {
        const key = typeof c.dataIndex === 'string'
          ? c.dataIndex
          : (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : c.key);
        const title = typeof c.title === 'string' ? c.title : (c.title?.props?.children ?? String(c.key || key || ''));
        return { key: key ? String(key) : undefined, title };
      })
      .filter((o: any) => o.key && !excluded.has(String(o.key)))
      .map((o: any) => ({ value: String(o.key), label: String(o.title) }));
  }, [columns]);

  return (
    <Card style={{ margin: '16px 0' }}>
      <Row align="middle" gutter={16}>
        <Col span={12}>
          <Title level={4} style={{ margin: 0 }}>Form Records (View)</Title>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Select
            style={{ minWidth: 220 }}
            placeholder="Select Form Group"
            value={selectedGroupId}
            onChange={val => {
              setSelectedGroupId(val);
              try {
                const moduleRoute = '/' + (module || '');
                const key = `form_details_selected_group:${moduleRoute}`;
                if (val == null) sessionStorage.removeItem(key);
                else sessionStorage.setItem(key, String(val));
              } catch {}
            }}
            allowClear
            options={groups.map(g => ({ value: g.id, label: g.group_name }))}
          />
        </Col>
      </Row>

      {/* Filters Row */}
      {selectedGroupId && (
        <Row gutter={4} style={{ marginTop: 12, marginBottom: 8 }}>
          <Col span={3}>
            <Select
              allowClear
              showSearch
              placeholder="Form Ref"
              style={{ width: '100%' }}
              value={filters.form_ref || undefined}
              onChange={(v) => setFilters(prev => ({ ...prev, form_ref: v || '' }))}
              options={formRefOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
            />
          </Col>
          <Col span={3}>
            <Select
              allowClear
              showSearch
              placeholder="Form Num"
              style={{ width: '100%' }}
              value={filters.form_num || undefined}
              onChange={(v) => setFilters(prev => ({ ...prev, form_num: v || '' }))}
              options={formNumOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
            />
          </Col>
          <Col span={3}>
            <DatePicker
              allowClear
              style={{ width: '100%' }}
              placeholder="Form Date"
              value={filters.form_date}
              onChange={(v) => setFilters(prev => ({ ...prev, form_date: v }))}
            />
          </Col>
          <Col span={3}>
            <Select
              showSearch
              mode="multiple"
              allowClear
              placeholder="Status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(vals) => setFilters(prev => ({ ...prev, status: Array.isArray(vals) ? vals : [] }))}
              options={statusOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
              optionRender={(option) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={filters.status.includes(String(option.value))} readOnly />
                  <span>{option.label as string}</span>
                </div>
              )}
            />
          </Col>
          <Col span={3}>
            <Select
              allowClear
              showSearch
              placeholder="Created By"
              style={{ width: '100%' }}
              value={filters.created_by || undefined}
              onChange={(v) => setFilters(prev => ({ ...prev, created_by: v || '' }))}
              options={createdByOptions.map((s: any) => ({ value: s, label: s }))}
              optionFilterProp="label"
            />
          </Col>
          {/* Column selector and query input */}
          <Col span={4}>
            <Select
              allowClear
              showSearch
              placeholder="Select Column"
              style={{ width: '100%' }}
              value={selectedColumn}
              onChange={(v) => setSelectedColumn(v)}
              options={columnOptions}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            />
          </Col>
          <Col span={5}>
            <input
              placeholder="Search in selected column"
              value={columnQuery}
              onChange={(e) => setColumnQuery(e.target.value)}
              style={{ width: '100%', height: 32, padding: '4px 8px', border: '1px solid #d9d9d9', borderRadius: 6 }}
            />
          </Col>
        </Row>
      )}

      {selectedGroupId ? (
        <StandardTable
          columns={columns}
          dataSource={dataWithStableKey}
          rowKey="_rowKey"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      ) : (
        <div style={{ margin: '32px 0', textAlign: 'center', color: '#888' }}>
          <b>Please select a Form Group to view records.</b>
        </div>
      )}
    </Card>
  );
};

export default FormRecordDetailsList;
